<script setup>
import { computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Icon from 'next/icon/Icon.vue';
import InsightCard from './InsightCard.vue';
import BrainBrandText from './BrainBrandText.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'agent',
    validator: v => ['agent', 'admin'].includes(v),
  },
});

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const insights = useMapGetter('captainInsights/getRecords');
const uiFlags = useMapGetter('captainInsights/getUIFlags');
const unreadCount = useMapGetter('captainInsights/getUnreadCount');
const meta = useMapGetter('captainInsights/getMeta');

const isFetching = computed(() => uiFlags.value.fetchingList);

const priorityOrder = { high: 0, medium: 1, low: 2 };

// Exclude conversation_summary — those belong in the conversation sidebar
const nonConversationInsights = computed(() =>
  insights.value.filter(i => i.insight_type !== 'conversation_summary')
);

// Agent mode: unread sorted by priority, limit 5
const agentInsights = computed(() =>
  nonConversationInsights.value
    .filter(i => i.status === 'unread')
    .sort(
      (a, b) =>
        (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1)
    )
    .slice(0, 5)
);

// Admin mode: daily_briefing first, then high priority alerts, then rest — limit 7
const adminInsights = computed(() => {
  const unread = nonConversationInsights.value.filter(
    i => i.status === 'unread'
  );
  const briefings = unread.filter(i => i.insight_type === 'daily_briefing');
  const highPriority = unread.filter(
    i => i.priority === 'high' && i.insight_type !== 'daily_briefing'
  );
  const rest = unread
    .filter(
      i => i.priority !== 'high' && i.insight_type !== 'daily_briefing'
    )
    .sort(
      (a, b) =>
        (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1)
    );
  return [...briefings, ...highPriority, ...rest].slice(0, 7);
});

const displayInsights = computed(() =>
  props.mode === 'admin' ? adminInsights.value : agentInsights.value
);

const hasInsights = computed(() => displayInsights.value.length > 0);

const totalCount = computed(() => meta.value?.totalCount || unreadCount.value);

const hasMore = computed(
  () => unreadCount.value > displayInsights.value.length
);

const viewAllLabel = computed(() => {
  if (hasMore.value && totalCount.value > 0) {
    return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.VIEW_ALL_COUNT', {
      count: totalCount.value,
    });
  }
  return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.VIEW_ALL');
});

const highPriorityCount = computed(
  () =>
    insights.value.filter(
      i => i.status === 'unread' && i.priority === 'high'
    ).length
);

// Last updated badge (most recent insight created_at)
const lastUpdated = computed(() => {
  if (!insights.value.length) return '';
  const latest = insights.value.reduce((max, i) => {
    const ts =
      typeof i.created_at === 'number'
        ? i.created_at
        : Date.parse(i.created_at) / 1000;
    return ts > max ? ts : max;
  }, 0);
  if (latest === 0) return '';
  const date = new Date(latest * 1000);
  const now = new Date();
  const diffMs = now - date;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1)
    return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.UPDATED_AGO', {
      time: t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.JUST_NOW'),
    });
  if (mins < 60)
    return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.UPDATED_AGO', {
      time: `${mins}m`,
    });
  const hours = Math.floor(mins / 60);
  if (hours < 24)
    return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.UPDATED_AGO', {
      time: `${hours}h`,
    });
  const days = Math.floor(hours / 24);
  return t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.UPDATED_AGO', {
    time: `${days}d`,
  });
});

const widgetTitle = computed(() =>
  props.mode === 'admin'
    ? t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.TITLE_ADMIN')
    : t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.TITLE')
);

const handleViewAll = () => {
  router.push({
    name: 'brain_insights',
    params: { accountId: route.params.accountId },
  });
};

const handleRefresh = () => {
  store.dispatch('captainInsights/get');
};

const handleMarkRead = id => {
  store.dispatch('captainInsights/markRead', id);
};

const handleDismiss = id => {
  store.dispatch('captainInsights/dismiss', id);
};

const handleItemClick = insight => {
  const data = insight.data || {};
  const accountId = route.params.accountId;
  if (data.deal_id) {
    router.push(`/app/accounts/${accountId}/crm/deals/${data.deal_id}`);
  } else if (data.conversation_id) {
    router.push(
      `/app/accounts/${accountId}/conversations/${data.conversation_id}`
    );
  } else {
    handleViewAll();
  }
};

onMounted(() => {
  store.dispatch('captainInsights/get');
});
</script>

<template>
  <div
    v-if="hasInsights || isFetching"
    class="rounded-xl border border-n-weak bg-n-background"
  >
    <!-- Header -->
    <div class="flex items-center justify-between px-5 pt-4 pb-3">
      <div class="flex items-center gap-2">
        <Icon icon="i-lucide-sparkles" class="size-4 text-n-brand" />
        <h3 class="text-sm font-medium text-n-slate-12">
          <BrainBrandText :text="widgetTitle" />
        </h3>
        <span
          v-if="unreadCount > 0"
          class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-n-brand/10 text-n-brand"
        >
          {{ unreadCount }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <span
          v-if="mode === 'admin' && lastUpdated"
          class="text-[10px] text-n-slate-10"
        >
          {{ lastUpdated }}
        </span>
        <span
          v-if="mode === 'admin' && highPriorityCount > 0"
          class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-r-50 text-r-700 dark:bg-r-900 dark:text-r-200"
        >
          {{ highPriorityCount }}
          {{ t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.HIGH_PRIORITY') }}
        </span>
        <button
          class="p-1 rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-black2 transition-colors"
          :title="t('CAPTAIN.BRAIN.INSIGHTS.WIDGET.REFRESH')"
          @click="handleRefresh"
        >
          <Icon icon="i-lucide-refresh-cw" class="size-3.5" />
        </button>
        <button
          class="text-xs text-b-600 hover:text-b-700 font-medium"
          @click="handleViewAll"
        >
          {{ viewAllLabel }}
        </button>
      </div>
    </div>

    <!-- Loading skeleton -->
    <div v-if="isFetching && !hasInsights" class="px-5 pb-4">
      <div class="space-y-3">
        <div
          v-for="i in 3"
          :key="i"
          class="h-14 rounded-lg bg-n-alpha-black2 animate-pulse"
        />
      </div>
    </div>

    <!-- Insight cards -->
    <div v-else-if="hasInsights" class="px-2 pb-3">
      <InsightCard
        v-for="insight in displayInsights"
        :key="insight.id"
        :insight="insight"
        compact
        @click="handleItemClick"
        @mark-read="handleMarkRead"
        @dismiss="handleDismiss"
      />

      <!-- Footer: View all with count -->
      <div
        v-if="hasMore"
        class="flex justify-end px-3 pt-2 border-t border-n-weak mt-1"
      >
        <button
          class="inline-flex items-center gap-1 text-xs text-b-600 hover:text-b-700 font-medium"
          @click="handleViewAll"
        >
          {{ viewAllLabel }}
          <Icon icon="i-lucide-arrow-right" class="size-3" />
        </button>
      </div>
    </div>
  </div>
</template>
